import { useEffect, useId, useRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { describeError } from "../api/errors";
import type { Tone } from "../lib/status";
import { Icon, type IconName } from "./Icon";

type BtnProps = ButtonHTMLAttributes<HTMLButtonElement> & { variant?: "primary" | "secondary" | "ghost" | "danger"; size?: "md" | "lg" | "sm"; icon?: IconName; busy?: boolean; block?: boolean };
export function Button({ variant = "secondary", size = "md", icon, busy, block, className = "", children, disabled, type = "button", ...rest }: BtnProps) {
  return (
    <button type={type} className={`btn btn-${variant} btn-${size}${block ? " btn-block" : ""} ${className}`} disabled={disabled || busy} aria-busy={busy || undefined} {...rest}>
      {busy ? <span className="spinner" aria-hidden /> : icon ? <Icon name={icon} size={20} /> : null}
      {children && <span>{children}</span>}
    </button>
  );
}

export function Badge({ tone = "neutral", children, dot }: { tone?: Tone; children: ReactNode; dot?: boolean }) {
  return <span className={`badge tone-${tone}`}>{dot && <span className="dot" aria-hidden />}{children}</span>;
}

export function Dot({ tone, label }: { tone: Tone; label: string }) {
  return <span className={`status-dot tone-${tone}`} role="img" aria-label={label} title={label} />;
}

export function Card({ title, action, children, className = "", tone, as: As = "section", id }: { title?: ReactNode; action?: ReactNode; children: ReactNode; className?: string; tone?: Tone; as?: "section" | "article" | "div"; id?: string }) {
  const hid = useId();
  return (
    <As className={`card ${tone ? `card-tone-${tone}` : ""} ${className}`} aria-labelledby={title ? hid : undefined} id={id}>
      {(title || action) && (
        <header className="card-head">
          {title && <h2 id={hid} className="card-title">{title}</h2>}
          {action}
        </header>
      )}
      {children}
    </As>
  );
}

export function PageHeader({ title, subtitle, back, actions }: { title: ReactNode; subtitle?: ReactNode; back?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="page-head">
      {back}
      <div className="page-head-row">
        <div className="page-head-text">
          <h1>{title}</h1>
          {subtitle && <p className="muted">{subtitle}</p>}
        </div>
        {actions && <div className="page-head-actions">{actions}</div>}
      </div>
    </header>
  );
}

export function EmptyState({ icon = "check", title, children, action }: { icon?: IconName; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <span className="empty-icon"><Icon name={icon} size={28} /></span>
      <h3>{title}</h3>
      {children && <p className="muted">{children}</p>}
      {action}
    </div>
  );
}

export function Loading({ label = "Loading…" }: { label?: string }) {
  return <div className="loading" role="status"><span className="spinner" aria-hidden /> <span>{label}</span></div>;
}

export function ErrorNote({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <div className="error-note" role="alert">
      <Icon name="alert" />
      <span>{describeError(error)}</span>
      {onRetry && <Button size="sm" variant="ghost" onClick={onRetry}>Try again</Button>}
    </div>
  );
}

/** Renders loading / error / data for a query result. */
export function QueryView<T>({ q, children, loading }: { q: { data: T | undefined; error: unknown; isLoading: boolean; refetch: () => unknown }; children: (data: T) => ReactNode; loading?: ReactNode }) {
  if (q.data !== undefined) return <>{q.error ? <ErrorNote error={q.error} onRetry={() => q.refetch()} /> : null}{children(q.data)}</>;
  if (q.error) return <ErrorNote error={q.error} onRetry={() => q.refetch()} />;
  return <>{loading ?? <Loading />}</>;
}

export function Segmented<V extends string>({ label, value, options, onChange, hideLabel }: { label: string; value: V; options: { value: V; label: ReactNode; hint?: string }[]; onChange: (v: V) => void; hideLabel?: boolean }) {
  const lid = useId();
  return (
    <div className="seg-wrap">
      <span id={lid} className={hideLabel ? "sr-only" : "field-label"}>{label}</span>
      <div className="seg" role="radiogroup" aria-labelledby={lid}>
        {options.map((o) => (
          <button key={o.value} type="button" role="radio" aria-checked={value === o.value} className="seg-btn" title={o.hint} onClick={() => onChange(o.value)}>
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function Chip({ pressed, onClick, children, tone, count }: { pressed: boolean; onClick: () => void; children: ReactNode; tone?: Tone; count?: number }) {
  return (
    <button type="button" className={`chip ${tone ? `tone-${tone}` : ""}`} aria-pressed={pressed} onClick={onClick}>
      {children}{count !== undefined && <span className="chip-count">{count}</span>}
    </button>
  );
}

export function Field({ label, hint, error, children, optional }: { label: string; hint?: ReactNode; error?: string; optional?: boolean; children: (id: string, describedBy: string | undefined) => ReactNode }) {
  const id = useId();
  const hid = `${id}-hint`;
  const eid = `${id}-err`;
  const describedBy = [hint ? hid : "", error ? eid : ""].filter(Boolean).join(" ") || undefined;
  return (
    <div className={`field ${error ? "field-invalid" : ""}`}>
      <label htmlFor={id} className="field-label">{label}{optional && <span className="muted"> · optional</span>}</label>
      {children(id, describedBy)}
      {hint && <p id={hid} className="field-hint">{hint}</p>}
      {error && <p id={eid} className="field-error" role="alert">{error}</p>}
    </div>
  );
}

export function Progress({ value, label, tone = "accent" }: { value: number; label: string; tone?: Tone }) {
  const v = Math.max(0, Math.min(1, value));
  return (
    <div className={`progress tone-${tone}`} role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(v * 100)}>
      <span style={{ width: `${v * 100}%` }} />
    </div>
  );
}

/** Modal base: focus moves in, Escape closes, focus returns to the opener. */
/** `panel` = a bottom sheet on phones and a right-hand side panel on desktop (post detail). */
export function Modal({ title, onClose, children, variant = "dialog", footer }: { title: string; onClose: () => void; children: ReactNode; variant?: "dialog" | "sheet" | "panel"; footer?: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const tid = useId();
  // Callers pass inline closures; keep the latest in a ref so a parent re-render (e.g. an SSE refetch) doesn't re-run the focus effect.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const onClose = () => closeRef.current();
    const opener = document.activeElement as HTMLElement | null;
    const first = ref.current?.querySelector<HTMLElement>("[data-autofocus], button, [href], input, textarea, select");
    first?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.stopPropagation(); onClose(); }
      if (e.key === "Tab" && ref.current) {
        const f = [...ref.current.querySelectorAll<HTMLElement>("button:not([disabled]), [href], input, textarea, select")];
        if (!f.length) return;
        const a = f[0]!, z = f[f.length - 1]!;
        if (e.shiftKey && document.activeElement === a) { e.preventDefault(); z.focus(); }
        else if (!e.shiftKey && document.activeElement === z) { e.preventDefault(); a.focus(); }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("keydown", onKey); opener?.focus?.(); };
  }, []);
  return (
    <div className={`overlay overlay-${variant}`} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={ref} className={`modal modal-${variant}`} role="dialog" aria-modal="true" aria-labelledby={tid}>
        <header className="modal-head">
          <h2 id={tid}>{title}</h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close"><Icon name="x" /></button>
        </header>
        <div className="modal-body">{children}</div>
        {footer && <footer className="modal-foot">{footer}</footer>}
      </div>
    </div>
  );
}

/** Destructive-action confirmation that states the impact (PRD §82), never just "Are you sure?". */
export function ConfirmDialog({ title, impact, confirmLabel, tone = "danger", busy, onConfirm, onCancel, children }: { title: string; impact: ReactNode; confirmLabel: string; tone?: "danger" | "primary"; busy?: boolean; onConfirm: () => void; onCancel: () => void; children?: ReactNode }) {
  return (
    <Modal title={title} onClose={onCancel} footer={
      <>
        <Button variant="ghost" onClick={onCancel} data-autofocus>Cancel</Button>
        <Button variant={tone === "danger" ? "danger" : "primary"} busy={busy} onClick={onConfirm}>{confirmLabel}</Button>
      </>
    }>
      <p className="impact">{impact}</p>
      {children}
    </Modal>
  );
}

/** An on/off setting: a checkbox with role="switch", label on the left, optional hint below. */
export function Switch({ label, checked, onChange, hint, disabled }: { label: string; checked: boolean; onChange: (v: boolean) => void; hint?: ReactNode; disabled?: boolean }) {
  const id = useId();
  return (
    <div className="switch-field">
      <label className="switch-row" htmlFor={id}>
        <span className="field-label">{label}</span>
        <input id={id} type="checkbox" role="switch" className="switch" checked={checked} disabled={disabled} aria-describedby={hint ? `${id}-hint` : undefined} onChange={(e) => onChange(e.target.checked)} />
      </label>
      {hint && <p id={`${id}-hint`} className="field-hint">{hint}</p>}
    </div>
  );
}
